import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { JobApplicationsFilterParams, JobApplicationType, JobApplicationsListResponse } from '@/types/job-application'
import { BulkDeleteResponse } from '@/types/general'
import { getErrorMessage } from '@/lib/utils'
import { fetchJobApplications, fetchJobApplication, deleteJobApplication, bulkDeleteJobApplications } from '@/lib/api'
import { SortEnum } from '@/enums'

type JobApplicationStateType = {
  isLoading: boolean
  response: JobApplicationType | null
  error: {
    status?: number
    message: string
  } | null
}

type JobApplicationsListStateType = {
  isLoading: boolean
  response: JobApplicationsListResponse | null
  error: {
    status?: number
    message: string
  } | null
}

type JobApplicationsStoreState = {
  filters: JobApplicationsFilterParams
  setFilters: (filters: Partial<JobApplicationsFilterParams>) => void
  resetFilters: () => void
  updateUrlParams: (searchParams: URLSearchParams) => void
  getUrlParams: () => URLSearchParams

  jobApplicationsListState: JobApplicationsListStateType
  fetchJobApplications: (params?: JobApplicationsFilterParams) => Promise<JobApplicationsListResponse>
  resetJobApplicationsList: () => void

  jobApplicationState: JobApplicationStateType
  getJobApplication: (id: string) => Promise<JobApplicationType>
  deleteJobApplication: (id: string) => Promise<void>
  resetJobApplication: () => void

  selectedJobApplicationIds: string[]
  selectJobApplication: (id: string) => void
  deselectJobApplication: (id: string) => void
  toggleJobApplicationSelection: (id: string) => void
  selectAllJobApplications: (items: JobApplicationType[]) => void
  deselectAllJobApplications: () => void
  isJobApplicationSelected: (id: string) => boolean

  bulkDeleteJobApplications: (ids: string[]) => Promise<BulkDeleteResponse>
}

const defaultFilters: JobApplicationsFilterParams = {
  page: 1,
  size: 20,
  sort: SortEnum.DESC,
  sortBy: 'createdAt'
}

const initialJobApplicationState: JobApplicationStateType = {
  isLoading: false,
  response: null,
  error: null
}

const initialJobApplicationsListState: JobApplicationsListStateType = {
  isLoading: false,
  response: null,
  error: null
}

const useJobApplicationsStore = create<JobApplicationsStoreState>()(
  devtools(
    (set, get) => ({
      filters: defaultFilters,

      setFilters: filters => {
        set(state => ({ filters: { ...state.filters, ...filters } }))
      },

      resetFilters: () => {
        set(() => ({ filters: defaultFilters }))
      },

      updateUrlParams: searchParams => {
        const filters = get().filters
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            searchParams.set(key, value.toString())
          }
        })
      },

      getUrlParams: () => {
        const filters = get().filters
        const params = new URLSearchParams()
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            params.set(key, value.toString())
          }
        })
        return params
      },

      jobApplicationsListState: initialJobApplicationsListState,

      fetchJobApplications: async params => {
        const queryParams = params ?? get().filters

        set(() => ({
          jobApplicationsListState: {
            isLoading: true,
            response: null,
            error: null
          }
        }))

        try {
          const response = await fetchJobApplications(queryParams)

          set(() => ({
            jobApplicationsListState: {
              isLoading: false,
              response,
              error: null
            }
          }))

          return response
        } catch (error: any) {
          set(() => ({
            jobApplicationsListState: {
              isLoading: false,
              response: null,
              error: {
                status: error.response?.status || 500,
                message: getErrorMessage(error)
              }
            }
          }))
          throw error
        }
      },

      resetJobApplicationsList: () => {
        set(() => ({ jobApplicationsListState: initialJobApplicationsListState }))
      },

      jobApplicationState: initialJobApplicationState,

      getJobApplication: async id => {
        set(() => ({
          jobApplicationState: {
            isLoading: true,
            response: null,
            error: null
          }
        }))

        try {
          const response = await fetchJobApplication(id)

          set(() => ({
            jobApplicationState: {
              isLoading: false,
              response,
              error: null
            }
          }))

          return response
        } catch (error: any) {
          set(() => ({
            jobApplicationState: {
              isLoading: false,
              response: null,
              error: {
                status: error.response?.status || 500,
                message: getErrorMessage(error)
              }
            }
          }))
          throw error
        }
      },

      deleteJobApplication: async id => {
        try {
          await deleteJobApplication(id)

          set(() => ({ jobApplicationState: initialJobApplicationState }))
        } catch (error: any) {
          set(() => ({
            jobApplicationState: {
              ...initialJobApplicationState,
              error: {
                status: error.response?.status || 500,
                message: getErrorMessage(error)
              }
            }
          }))
          throw error
        }
      },

      resetJobApplication: () => {
        set(() => ({ jobApplicationState: initialJobApplicationState }))
      },

      selectedJobApplicationIds: [],

      selectJobApplication: id => {
        set(state => {
          if (state.selectedJobApplicationIds.includes(id)) {
            return state
          }
          return { selectedJobApplicationIds: [...state.selectedJobApplicationIds, id] }
        })
      },

      deselectJobApplication: id => {
        set(state => ({
          selectedJobApplicationIds: state.selectedJobApplicationIds.filter(i => i !== id)
        }))
      },

      toggleJobApplicationSelection: id => {
        set(state => {
          if (state.selectedJobApplicationIds.includes(id)) {
            return { selectedJobApplicationIds: state.selectedJobApplicationIds.filter(i => i !== id) }
          }
          return { selectedJobApplicationIds: [...state.selectedJobApplicationIds, id] }
        })
      },

      selectAllJobApplications: items => {
        set(() => ({ selectedJobApplicationIds: items.map(i => i.id) }))
      },

      deselectAllJobApplications: () => {
        set(() => ({ selectedJobApplicationIds: [] }))
      },

      isJobApplicationSelected: id => {
        return get().selectedJobApplicationIds.includes(id)
      },

      bulkDeleteJobApplications: async ids => {
        set(state => ({
          jobApplicationState: {
            ...state.jobApplicationState,
            isLoading: true,
            error: null
          }
        }))

        try {
          const response = await bulkDeleteJobApplications({ ids })

          set(state => ({
            jobApplicationState: {
              ...state.jobApplicationState,
              isLoading: false
            },
            selectedJobApplicationIds: state.selectedJobApplicationIds.filter(id => !response.deletedItems.includes(id))
          }))

          return response
        } catch (error: any) {
          set(state => ({
            jobApplicationState: {
              ...state.jobApplicationState,
              error: {
                status: error.response?.status || 500,
                message: getErrorMessage(error)
              },
              isLoading: false
            }
          }))
          throw error
        }
      }
    }),
    { name: 'job-applications-store' }
  )
)

export default useJobApplicationsStore
